import {SubmitCRMEventTokenResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useVoucherConditionPage} from '@aboutyou/router';
import {
  Button,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  Text,
  UnstyledLink,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';

export const CampaignConfirmationView: React.FC<
  SubmitCRMEventTokenResponse
> = ({errorMessages}) => {
  const {t} = useTranslation();

  const hasError = errorMessages.length > 0;
  const voucherConditionsUrl = useVoucherConditionPage();

  return (
    <Wrapper>
      <InfoMessage>
        <Icon
          asset={
            hasError ? GenericIconName.Error : GenericIconName.AlertSuccessFill
          }
          size="XL"
          fill={hasError ? COLORS.Red : COLORS.Green3}
        />
        <Text
          data-testid="campaignConfirmationTitle"
          size="px24"
          weight="heavy"
        >
          {hasError
            ? t('Oops! Something went wrong!')
            : t('Yeah, your registration was successful!')}
        </Text>
        {!hasError && (
          <>
            <Text data-testid="campaignConfirmationSubtitle">
              {t(
                'We will get in touch with you if you are the lucky one! Stay tuned & good luck! 🤞',
              )}
            </Text>
            <TermsAndConditions to={voucherConditionsUrl}>
              {t('Terms & Conditions')}
            </TermsAndConditions>
          </>
        )}
      </InfoMessage>
      <Footer>
        <Text color="Grey0">
          {t('In the meanwhile, check out current Trends, Drops and more')}
        </Text>
        <StyledButton
          data-testid="campaignConfirmationCTA"
          href="/"
          variant="secondary"
          size="fit-width"
        >
          {t('Go to the shop')}
        </StyledButton>
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  ${tabletAndUp(
    css`
      height: 80vh;
      gap: 32px;
      padding-top: 32px;
      background: ${COLORS.Grey5};
      overflow: hidden;
    `,
  )};
`;

const InfoMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 520px;
  gap: 12px;
  line-height: 1.5;
  padding: 64px 32px;

  ${tabletAndUp(
    css`
      padding: 32px 32px 48px;
      border-radius: 16px;
      background: ${COLORS.White};
      border: 1px solid ${COLORS.Grey2};
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.06),
        0px 8px 24px 2px rgba(0, 0, 0, 0.06);
    `,
  )}
`;

const TermsAndConditions = styled(UnstyledLink)`
  font-size: ${FONT_SIZES.px10};
  text-decoration: underline;
  margin-top: 2rem;
  &&& {
    color: ${COLORS.Grey1};
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 32px 24px;
  border-top: 8px solid ${COLORS.Grey5};

  ${tabletAndUp(
    css`
      border-top: unset;
    `,
  )}
`;

const StyledButton = styled(Button)`
  &&&& {
    padding: 14px 32px;
    --color: ${COLORS.Black};
    --background-color: transparent;
  }
`;
